import { CurityCredentials, TokenResponse, currentEnvAppConfig } from '../../utils';
import { Credentials, transformCredentials } from './transformers/credentials';
import {
  AuthorizeCodeParams,
  CurityConfig,
  EmptyRefreshResponseError,
  OAuthError,
} from './types/curity';

const OAUTH_TIMEOUT_MS = 15_000;

const { curityRedirectUri, curityOauthBaseUrl, curityClientId } =
  currentEnvAppConfig;

export class CurityApi {
  private baseURL: string;
  private clientId: string;

  constructor(config: CurityConfig) {
    if (!config.baseURL) {
      throw new Error('Missing baseURL for CurityApi');
    }
    this.clientId = config.clientId;
    this.baseURL = config.baseURL;
  }

  public setUrl(config: CurityConfig) {
    this.baseURL = config.baseURL;
    this.clientId = config.clientId;
  }

  public async authorizeCode(params: AuthorizeCodeParams) {
    const redirectUri = params.redirectUri ?? curityRedirectUri ?? '';
    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      redirect_uri: redirectUri,
      code: params.code,
      code_verifier: params.codeVerifier,
      client_id: this.clientId,
    });

    const response = await fetch(`${this.baseURL}/oauth-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
      signal: AbortSignal.timeout(OAUTH_TIMEOUT_MS),
    });

    if (!response.ok) {
      let detail = '';
      try {
        detail = await response.text();
      } catch {
        // ignore unreadable body
      }
      throw new Error(
        `OAuth request failed: ${response.status} ${response.statusText}${detail ? ` — ${detail}` : ''}`
      );
    }

    const data: TokenResponse = await response.json();
    if ('error' in data) {
      throw new Error(data.error);
    }
    return transformCredentials(data);
  }

  private async performTokenRequest(body: URLSearchParams): Promise<Response> {
    return fetch(`${this.baseURL}/oauth-token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
      signal: AbortSignal.timeout(OAUTH_TIMEOUT_MS),
    });
  }

  private async handleFailedResponse(response: Response): Promise<never> {
    let detail = '';
    try {
      detail = await response.text();
    } catch {
      // ignore unreadable body
    }
    // RFC 6749 §5.2 error responses are JSON; keep the code so the caller can
    // distinguish a dead session from a transient outage.
    let oauthErrorCode: string | undefined;
    if (detail) {
      try {
        const parsed: unknown = JSON.parse(detail);
        if (typeof parsed === 'object' && parsed !== null && 'error' in parsed) {
          const code = (parsed as { error: unknown }).error;
          if (typeof code === 'string') oauthErrorCode = code;
        }
      } catch {
        // non-JSON error body — status alone drives classification
      }
    }
    throw new OAuthError(
      `OAuth refresh failed: ${response.status} ${response.statusText}${detail ? ` — ${detail}` : ''}`,
      response.status,
      oauthErrorCode
    );
  }

  private parseTokenResponse(text: string): CurityCredentials {
    if (!text) {
      throw new EmptyRefreshResponseError();
    }

    let data: TokenResponse;
    try {
      data = JSON.parse(text) as TokenResponse;
    } catch {
      // A garbled 200 is an infrastructure problem, not a rejected grant — throw the
      // shared type so every caller classifies it as transient rather than terminal.
      throw new OAuthError(
        `OAuth refresh failed: server returned invalid JSON — ${text.slice(0, 200)}`,
        200
      );
    }

    if ('error' in data) {
      throw new OAuthError(data.error, 200, data.error);
    }

    return data as CurityCredentials;
  }

  private async attemptTokenRefresh(body: URLSearchParams): Promise<Credentials> {
    const response = await this.performTokenRequest(body);

    if (!response.ok) {
      await this.handleFailedResponse(response);
    }

    const text = await response.text();
    const data = this.parseTokenResponse(text);
    return transformCredentials(data);
  }

  public async refreshToken(credentials: Credentials) {
    if (!credentials) {
      throw new Error('No credentials in storage');
    }

    const body = new URLSearchParams({
      client_id: this.clientId,
      grant_type: 'refresh_token',
      refresh_token: credentials.refreshToken,
    });

    try {
      return await this.attemptTokenRefresh(body);
    } catch (e) {
      if (e instanceof EmptyRefreshResponseError) {
        // Retry once — Curity occasionally returns 200 with empty body on first attempt
        return await this.attemptTokenRefresh(body);
      }
      throw e;
    }
  }
}

export const curityApiInstance = new CurityApi({
  baseURL: curityOauthBaseUrl,
  clientId: curityClientId,
});
